import { IVector3 } from '../api.js';
import { clamp, matrix_pool } from '../Math.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Plane as BABYLON_Plane } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.plane.js';
import { Ray as BABYLON_Ray } from '@inter-ikea-betrakta-babylonjs/core/Culling/ray.js';
import { raycast_interactable_object } from '../babylon/Raycast.js';
export var InteractionType;
(function (InteractionType) {
    InteractionType[InteractionType["INVALID"] = -1] = "INVALID";
    InteractionType[InteractionType["TOUCH"] = 0] = "TOUCH";
    InteractionType[InteractionType["PRIMARY"] = 1] = "PRIMARY";
    InteractionType[InteractionType["SECONDARY"] = 2] = "SECONDARY";
})(InteractionType || (InteractionType = {}));
/**
 * InputManagers responsibility is to handle the decision of which entity is to receive input sent to the viewer.
 * It receives normalized input, so gestures and such is expected to have already been handled.
 */
export class InputManager {
    get receiving_object() {
        if (this._pick_result) {
            return this._pick_result[0];
        }
        return null;
    }
    get pick_info() {
        if (this._pick_result) {
            return this._pick_result[1];
        }
        return null;
    }
    constructor(_view, _viewer, _click_void_observable, _secondary_click_void_observable) {
        this._view = _view;
        this._viewer = _viewer;
        this._click_void_observable = _click_void_observable;
        this._secondary_click_void_observable = _secondary_click_void_observable;
        // The SceneObject that a given input will act on, it is picked with interaction_begin event and kept for the entire action.
        // If it set to null, the camera becomes the target of the action.
        this._pick_result = null;
        // The objects that receive the input events from the initially picked interactable object.
        this._execute_objects = [];
        this._last_drag_point = new BABYLON_Vector3();
        this._new_drag_point = new BABYLON_Vector3();
        this._begin_drag_point = new BABYLON_Vector3(); // Note that this vector is only updated between calls
        this._drag_distance = 0;
        this._picking_ray = BABYLON_Ray.Zero();
        this._camera_input_enabled = true;
        this._last_mouse_position = { x: 0, y: 0 };
        this._received_mouse_move_event = false;
        this._last_hover_object = null;
        this._interactable_disposed_observer = this._viewer.interactable_disposed_observable.add((interactable) => {
            if (interactable === this._last_hover_object) {
                this.clear_hovering_interactable();
            }
        });
    }
    get camera_input_enabled() {
        return this._camera_input_enabled;
    }
    set camera_input_enabled(value) {
        this._camera_input_enabled = value;
    }
    /**
     * This event is used for things like picking so that you can pick a receiver before you know what kind of event it will be.
     * For example you might want to wait for the pointer to have moved over a threshold before making it a drag.
     * But you might still want to pick the object that was under the pointer when it was first pressed.
     * @param evt position for where the interaction began.
     */
    interaction_begin(evt) {
        const view_viewport = this._view.get_canvas_viewport();
        this._view.create_ray_to_ref(clamp((evt.canvas_x - view_viewport.x) / view_viewport.width, 0.0, 1.0), clamp((evt.canvas_y - view_viewport.y) / view_viewport.height, 0.0, 1.0), this._picking_ray);
        this._pick_result = raycast_interactable_object(this._viewer, this._picking_ray, this._view.get_render_layer_mask());
        if (this._pick_result !== null) {
            this._execute_objects = this._viewer.get_input_handler_manager().get_execute_objects(this._pick_result[0]);
        }
        else {
            this._execute_objects = [];
        }
    }
    interaction_end() {
        this._pick_result = null;
        this._execute_objects = [];
    }
    drag_begin(evt) {
        if (this._pick_result === null) {
            this.camera_drag_start(evt);
            return;
        }
        const [, interactable_raycast_result] = this._pick_result;
        if (this._execute_objects.length === 0) {
            this.interaction_end();
            return;
        }
        let execution_object_has_drag_interactable = false;
        this._execute_objects.forEach((execute_object) => {
            if (!execute_object.drag_interactable)
                return;
            execution_object_has_drag_interactable = true;
            this._drag_distance = 0;
            const drag_event = {
                canvas_x: evt.canvas_x,
                canvas_y: evt.canvas_y,
                plane_point: interactable_raycast_result.world_position, // The point in space where the collision happened
                plane_normal: this._view.engine_camera.backward,
                world_delta: { x: 0, y: 0, z: 0 },
                local_delta: { x: 0, y: 0, z: 0 },
                world_distance: 0,
                camera_position: this._view.engine_camera.position
            };
            this._begin_drag_point.fromIVector3(interactable_raycast_result.world_position);
            this._last_drag_point.fromIVector3(interactable_raycast_result.world_position);
            if ('id' in execute_object) {
                execute_object.drag_begin_observable.notify(execute_object.id, drag_event);
            }
            else if ('mesh_source' in execute_object) {
                execute_object.drag_begin_observable.notify(execute_object, drag_event);
            }
            else {
                console.error('Cannot notify observable from interactable', execute_object);
            }
        });
        if (!execution_object_has_drag_interactable) {
            // No excecution object was draggable, so we reset this drag action, so that the camera can consume it
            this._pick_result = null;
            this.camera_drag_start(evt);
        }
    }
    get_local_drag_event(world_drag_event, execute_object) {
        if (!execute_object.parent) {
            return Object.assign({}, world_drag_event);
        }
        const matrix = matrix_pool.fetch();
        execute_object.parent.root.getWorldMatrix().invertToRef(matrix);
        const local_delta = new BABYLON_Vector3(world_drag_event.world_delta.x, world_drag_event.world_delta.y, world_drag_event.world_delta.z);
        BABYLON_Vector3.TransformNormalToRef(local_delta, matrix, local_delta);
        matrix_pool.return(matrix);
        return Object.assign(Object.assign({}, world_drag_event), { local_delta: local_delta });
    }
    drag_move(evt) {
        if (this._pick_result === null) {
            this.camera_drag_move(evt);
            return;
        }
        const [, interactable_raycast_result] = this._pick_result;
        if (interactable_raycast_result) {
            const world_drag_event = this.generate_drag_event(evt.canvas_x, evt.canvas_y);
            this._execute_objects.forEach((execute_object) => {
                if (!execute_object.drag_interactable)
                    return;
                const local_drag_event = this.get_local_drag_event(world_drag_event, execute_object);
                if ('id' in execute_object) {
                    execute_object.drag_observable.notify(execute_object.id, local_drag_event);
                }
                else if ('mesh_source' in execute_object) {
                    execute_object.drag_observable.notify(execute_object, local_drag_event);
                }
                else {
                    console.error('Cannot notify observable from interactable', execute_object);
                }
            });
        }
    }
    drag_end(evt) {
        if (this._pick_result === null) {
            this.camera_drag_end(evt);
            return;
        }
        const [, interactable_raycast_result] = this._pick_result;
        if (interactable_raycast_result) {
            const world_drag_event = this.generate_drag_event(evt.canvas_x, evt.canvas_y);
            this._execute_objects.forEach((execute_object) => {
                if (!execute_object.drag_interactable)
                    return;
                const local_drag_event = this.get_local_drag_event(world_drag_event, execute_object);
                if ('id' in execute_object) {
                    execute_object.drag_end_observable.notify(execute_object.id, local_drag_event);
                }
                else if ('mesh_source' in execute_object) {
                    execute_object.drag_end_observable.notify(execute_object, local_drag_event);
                }
                else {
                    console.error('Cannot notify observable from interactable', execute_object);
                }
                this.interaction_end();
                this._last_drag_point.copyFromFloats(0, 0, 0);
                this._new_drag_point.copyFromFloats(0, 0, 0);
            });
        }
    }
    click(evt) {
        const click_event = {
            canvas_x: evt.canvas_x,
            canvas_y: evt.canvas_y,
            duration_ms: evt.duration_ms,
            world_point: null,
            camera_position: this._view.engine_camera.position
        };
        let called_observer = false;
        this._execute_objects.forEach((execute_object) => {
            if (!execute_object.click_interactable || this._pick_result === null)
                return;
            click_event.world_point = IVector3.copy(this._pick_result[1].world_position);
            if ('id' in execute_object) {
                const observable = evt.type === InteractionType.SECONDARY
                    ? execute_object.secondary_click_observable
                    : execute_object.click_observable;
                observable.notify(execute_object.id, click_event);
                if (observable.has_active_observers) {
                    called_observer = true;
                }
            }
            else if ('mesh_source' in execute_object) {
                const observable = evt.type === InteractionType.SECONDARY
                    ? execute_object.secondary_click_observable
                    : execute_object.click_observable;
                observable.notify(execute_object, click_event);
                if (observable.has_active_observers) {
                    called_observer = true;
                }
            }
            else {
                console.error('Cannot notify observable from interactable', execute_object);
            }
        });
        if (!called_observer) {
            const observable = evt.type === InteractionType.SECONDARY
                ? this._secondary_click_void_observable
                : this._click_void_observable;
            observable.notify(this._viewer, click_event);
        }
        this.interaction_end();
    }
    pan_move(evt) {
        var _a;
        if (!this._camera_input_enabled)
            return;
        (_a = this._view.camera_manager.active_camera) === null || _a === void 0 ? void 0 : _a.pan_input(evt);
    }
    pan_begin(evt) {
        var _a;
        if (!this._camera_input_enabled)
            return;
        (_a = this._view.camera_manager.active_camera) === null || _a === void 0 ? void 0 : _a.pan_start(evt);
    }
    pan_end(evt) {
        var _a;
        if (!this._camera_input_enabled)
            return;
        (_a = this._view.camera_manager.active_camera) === null || _a === void 0 ? void 0 : _a.pan_end(evt);
    }
    zoom(evt) {
        var _a;
        if (!this._camera_input_enabled)
            return;
        (_a = this._view.camera_manager.active_camera) === null || _a === void 0 ? void 0 : _a.zoom_input(evt);
    }
    camera_drag_move(evt) {
        var _a;
        if (!this._camera_input_enabled)
            return;
        (_a = this._view.camera_manager.active_camera) === null || _a === void 0 ? void 0 : _a.drag_input(evt);
    }
    camera_drag_start(evt) {
        var _a;
        if (!this._camera_input_enabled)
            return;
        (_a = this._view.camera_manager.active_camera) === null || _a === void 0 ? void 0 : _a.drag_start(evt);
    }
    camera_drag_end(evt) {
        var _a;
        if (!this._camera_input_enabled)
            return;
        (_a = this._view.camera_manager.active_camera) === null || _a === void 0 ? void 0 : _a.drag_end(evt);
    }
    generate_drag_event(client_x, client_y) {
        // We should be able to expect backwards to always be normalized.
        const normal = this._view.engine_camera.backward;
        const plane = new BABYLON_Plane(normal.x, normal.y, normal.z, -(normal.x * this._begin_drag_point.x + normal.y * this._begin_drag_point.y + normal.z * this._begin_drag_point.z));
        const view_viewport = this._view.get_canvas_viewport();
        this._view.create_ray_to_ref(clamp((client_x - view_viewport.x) / view_viewport.width, 0.0, 1.0), clamp((client_y - view_viewport.y) / view_viewport.height, 0.0, 1.0), this._picking_ray);
        const hit_distance = this._picking_ray.intersectsPlane(plane);
        const hit_point = this._new_drag_point
            .copyFrom(this._picking_ray.direction)
            .scaleInPlace(hit_distance)
            .addInPlace(this._picking_ray.origin);
        const world_delta = hit_point.subtract(this._last_drag_point);
        this._drag_distance += world_delta.length();
        const local_delta = world_delta.clone();
        const drag_event = {
            canvas_x: client_x,
            canvas_y: client_y,
            plane_point: hit_point,
            plane_normal: normal,
            world_delta: world_delta,
            local_delta: local_delta,
            world_distance: this._drag_distance,
            camera_position: this._view.engine_camera.position
        };
        this._last_drag_point.copyFrom(hit_point);
        return drag_event;
    }
    mouse_pointer_move(event) {
        this._last_mouse_position.x = event.canvas_x;
        this._last_mouse_position.y = event.canvas_y;
        this._received_mouse_move_event = true;
    }
    clear_hovering_interactable() {
        if (this._last_hover_object === null)
            return;
        const event = {
            canvas_x: this._last_mouse_position.x,
            canvas_y: this._last_mouse_position.y
        };
        const execute_objects = this._viewer.get_input_handler_manager().get_execute_objects(this._last_hover_object);
        execute_objects.forEach((execute_object) => {
            if (this._last_hover_object === null)
                return;
            if ('id' in execute_object) {
                execute_object.pointer_leave_observable.notify(execute_object.id, event);
            }
            else if ('mesh_source' in execute_object) {
                execute_object.pointer_leave_observable.notify(execute_object, event);
            }
            else {
                console.error('Cannot notify observable from interactable', execute_object);
            }
        });
        this._last_hover_object = null;
    }
    update_pointer_enter_leave() {
        if (!this._received_mouse_move_event) {
            return;
        }
        const view_viewport = this._view.get_canvas_viewport();
        this._view.create_ray_to_ref(clamp((this._last_mouse_position.x - view_viewport.x) / view_viewport.width, 0.0, 1.0), clamp((this._last_mouse_position.y - view_viewport.y) / view_viewport.height, 0.0, 1.0), this._picking_ray);
        const raycast_result = this._viewer.raycast_interactable_using_ray(this._picking_ray);
        if (raycast_result === null) {
            this.clear_hovering_interactable();
            return;
        }
        const picked_object = typeof raycast_result.object === 'number'
            ? this._viewer.viewer_scene.objects.get(raycast_result.object)
            : raycast_result.object;
        const execute_objects = this._viewer.get_input_handler_manager().get_execute_objects(picked_object);
        if (this._last_hover_object === null || this._last_hover_object !== picked_object) {
            this.clear_hovering_interactable();
            this._last_hover_object = picked_object;
            execute_objects.forEach((execute_object) => {
                if (this._last_hover_object === null) {
                    return;
                }
                const event = {
                    canvas_x: this._last_mouse_position.x,
                    canvas_y: this._last_mouse_position.y
                };
                if ('id' in execute_object) {
                    execute_object.pointer_enter_observable.notify(execute_object.id, event);
                }
                else if ('mesh_source' in execute_object) {
                    execute_object.pointer_enter_observable.notify(execute_object, event);
                }
                else {
                    console.error('Cannot notify observable from interactable', execute_object);
                }
            });
        }
    }
    dispose() {
        this._execute_objects.length = 0;
        this._pick_result = null;
        this._last_hover_object = null;
    }
}
//# sourceMappingURL=InputManager.js.map